import React, { useState, useEffect } from 'react';
import { Clock, DollarSign, Check } from 'lucide-react';
import { bookingWizardService } from '../../services/bookingWizardService';
import { WizardData, ProfessionalService } from '../../types/bookingWizard.types';

interface ServiceSelectionStepProps {
    professionalId: number;
    data: WizardData;
    onUpdate: (data: Partial<WizardData>) => void;
    onNext: () => void;
    onBack?: () => void;
}

export const ServiceSelectionStep: React.FC<ServiceSelectionStepProps> = ({
    professionalId,
    data,
    onUpdate,
    onNext,
    onBack
}) => {
    const [services, setServices] = useState<ProfessionalService[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        loadServices();
    }, [professionalId]);

    const loadServices = async () => {
        setLoading(true);
        try {
            const data = await bookingWizardService.getProfessionalServices(professionalId);
            setServices(data);
        } catch (err) {
            setError('Erro ao carregar serviços');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectService = (service: ProfessionalService) => {
        onUpdate({
            service: {
                id: service.service,
                name: service.service_name,
                duration: service.service_duration,
                price: service.custom_price || service.default_price
            }
        });
    };

    const formatDuration = (duration: string): string => {
        const match = duration.match(/(\d+):(\d+):\d+/);
        if (match) {
            const hours = parseInt(match[1]);
            const minutes = parseInt(match[2]);
            if (hours > 0) {
                return `${hours}h ${minutes > 0 ? minutes + 'min' : ''}`;
            }
            return `${minutes} min`;
        }
        return duration;
    };

    const formatPrice = (price: string): string => {
        const num = parseFloat(price);
        return num.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    };

    if (loading) {
        return (
            <div className="step-container">
                <h3>Carregando serviços...</h3>
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <div className="step-container">
            <h3>Selecionar Serviço</h3>
            <p>Escolha o serviço para o agendamento</p>

            {services.length === 0 ? (
                <div className="error-message">
                    Nenhum serviço configurado para este profissional.
                </div>
            ) : (
                <div className="services-list">
                    {services.map((service) => {
                        const isSelected = data.service?.id === service.service;
                        const price = service.custom_price || service.default_price;

                        return (
                            <div
                                key={service.id}
                                className={`service-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleSelectService(service)}
                            >
                                <div className="service-info">
                                    <h4>{service.service_name}</h4>
                                    <div className="service-details">
                                        <span className="detail-item">
                                            <Clock size={16} />
                                            {formatDuration(service.service_duration)}
                                        </span>
                                        <span className="detail-item">
                                            <DollarSign size={16} />
                                            {formatPrice(price)}
                                        </span>
                                    </div>
                                </div>
                                {isSelected && <Check className="check-icon" />}
                            </div>
                        );
                    })}
                </div>
            )}

            {error && <div className="error-message">{error}</div>}

            <div className="step-navigation">
                <button className="btn-secondary" onClick={onBack}>
                    Voltar
                </button>
                <button
                    className="btn-primary"
                    onClick={onNext}
                    disabled={!data.service}
                >
                    Continuar
                </button>
            </div>
        </div>
    );
};

export default ServiceSelectionStep;
